/**
 * Particle system — death shatter, landing impact, graze sparks,
 * ambient motes, beat pulses, collect bursts.
 *
 * Lightweight particle manager that updates and renders small
 * colored fragments. Each particle has its own gravity value.
 */

import type { Color } from '@/types';
import type { Renderer } from '@/rendering/renderer';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  size: number;
  color: Color;
  life: number;
  maxLife: number;
  gravity: number;
}

export class ParticleSystem {
  particles: Particle[] = [];

  /** Spawn a death shatter — triangle breaks into fragments */
  spawnShatter(x: number, y: number, color: Color, count = 8): void {
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const speed = 150 + Math.random() * 250;
      this.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 15,
        size: 3 + Math.random() * 5,
        color: { ...color, a: 1 },
        life: 0.6 + Math.random() * 0.4,
        maxLife: 0.6 + Math.random() * 0.4,
        gravity: -2000,
      });
    }
  }

  /** Spawn landing impact particles */
  spawnLandingImpact(x: number, y: number, color: Color): void {
    const count = 5;
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI * 0.3 + (Math.PI * 0.6 * i) / (count - 1);
      const speed = 60 + Math.random() * 80;
      this.particles.push({
        x: x + (Math.random() - 0.5) * 10, y,
        vx: Math.cos(angle) * speed,
        vy: Math.abs(Math.sin(angle)) * speed * 0.5,
        rotation: 0, rotationSpeed: 0,
        size: 1.5 + Math.random() * 2,
        color: { ...color, a: 0.8 },
        life: 0.25 + Math.random() * 0.15,
        maxLife: 0.25 + Math.random() * 0.15,
        gravity: -1500,
      });
    }
  }

  /** Spawn collect burst (star/fragment pickup) */
  spawnCollect(x: number, y: number, color: Color): void {
    const count = 12;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count;
      const speed = 80 + Math.random() * 120;
      this.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 8,
        size: 1.5 + Math.random() * 2.5,
        color: { ...color, a: 1 },
        life: 0.3 + Math.random() * 0.2,
        maxLife: 0.3 + Math.random() * 0.2,
        gravity: 0,
      });
    }
  }

  /** Spawn ambient floating motes of light */
  spawnAmbient(
    x: number, y: number, viewWidth: number, viewHeight: number, color: Color,
  ): void {
    const px = x + (Math.random() - 0.5) * viewWidth;
    const py = y + (Math.random() - 0.5) * viewHeight;
    this.particles.push({
      x: px, y: py,
      vx: (Math.random() - 0.5) * 15,
      vy: 10 + Math.random() * 20,
      rotation: 0, rotationSpeed: 0,
      size: 0.5 + Math.random() * 1.5,
      color: { ...color, a: 0.15 + Math.random() * 0.15 },
      life: 2 + Math.random() * 3,
      maxLife: 2 + Math.random() * 3,
      gravity: 0,
    });
  }

  /** Spawn beat pulse particles from edges */
  spawnBeatPulse(
    x: number, y: number, viewWidth: number, viewHeight: number,
    color: Color, strength: number,
  ): void {
    const count = Math.floor(6 + strength * 12);
    for (let i = 0; i < count; i++) {
      const fromLeft = Math.random() > 0.5;
      const px = fromLeft ? x - viewWidth * 0.5 : x + viewWidth * 0.5;
      const py = y + (Math.random() - 0.5) * viewHeight;
      const speed = 40 + Math.random() * 60;
      this.particles.push({
        x: px, y: py,
        vx: (fromLeft ? 1 : -1) * speed,
        vy: (Math.random() - 0.5) * 30,
        rotation: 0, rotationSpeed: 0,
        size: 1 + Math.random() * 2,
        color: { ...color, a: 0.3 * strength },
        life: 0.5 + Math.random() * 0.5,
        maxLife: 0.5 + Math.random() * 0.5,
        gravity: 0,
      });
    }
  }

  /** Spawn graze sparks (near-miss visual) */
  spawnGraze(x: number, y: number, color: Color): void {
    const count = 3;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 100 + Math.random() * 100;
      this.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rotation: 0, rotationSpeed: 0,
        size: 1 + Math.random() * 1.5,
        color: { r: 1, g: 1, b: 1, a: 1 },
        life: 0.15 + Math.random() * 0.1,
        maxLife: 0.15 + Math.random() * 0.1,
        gravity: 0,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += p.gravity * dt;
      p.rotation += p.rotationSpeed * dt;
      p.vx *= 0.98;
    }
  }

  render(renderer: Renderer): void {
    for (const p of this.particles) {
      const alpha = (p.life / p.maxLife);
      const color: Color = { ...p.color, a: p.color.a * alpha };
      const s = p.size * alpha;
      const cos = Math.cos(p.rotation);
      const sin = Math.sin(p.rotation);

      const pts: [number, number][] = [
        [s, 0],
        [-s * 0.5, s * 0.866],
        [-s * 0.5, -s * 0.866],
      ];

      const transformed = pts.map(([px, py]) => ({
        x: p.x + px * cos - py * sin,
        y: p.y + px * sin + py * cos,
      }));

      renderer.drawTriangle(
        transformed[0].x, transformed[0].y,
        transformed[1].x, transformed[1].y,
        transformed[2].x, transformed[2].y,
        color,
      );
    }
  }

  clear(): void {
    this.particles = [];
  }
}
